import {
    FunctionComponent,
    ReactElement,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import { Icon, IconButton, styled } from "@mui/material";

const Root = styled("div")`
    position: relative;
`;

interface IVideoProps {
    width?: string;
    height?: string;
    borderRadius?: string;
    aspectRadio?: string;
}

const Video = styled("video")<IVideoProps>`
    width: ${({ width }) => width ?? "100%"};
    height: ${({ height }) => height ?? "100%"};

    aspect-ratio: ${({ aspectRadio }) => aspectRadio ?? "16 / 9"};

    border-radius: ${({ borderRadius }) => borderRadius ?? "0px"};

    object-fit: cover;
`;

const Play = styled(IconButton)`
    position: absolute;
    top: calc(50% - 40px);
    left: calc(50% - 40px);
    width: 80px;
    height: 80px;

    z-index: 1;

    color: white;
    text-align: center;
`;

export interface IProductVideoProps {
    source: string;
    autoPlay: boolean;
    muted: boolean;
    loop?: boolean;
    poster?: string;
    width?: string;
    height?: string;
    stopPlaying?: boolean;
    borderRadius?: string;
    aspectRadio?: string;
}

export const ProductVideo: FunctionComponent<IProductVideoProps> = (
    props,
): ReactElement => {
    const {
        source,
        autoPlay,
        muted,
        poster,
        width,
        height,
        stopPlaying = false,
        loop = false,
        borderRadius,
        aspectRadio,
    } = props;

    const [playing, setPlaying] = useState(autoPlay);

    const ref = useRef<any>();

    const handlePlay = useCallback(() => {
        const element = ref.current;
        if (!element) {
            return;
        }

        setPlaying(!playing);

        if (playing) {
            element.pause();
        } else {
            element.play();
        }
    }, [playing]);

    useEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }

        if (playing && stopPlaying) {
            element.pause();
            setPlaying(!playing);
        }
    }, [playing, stopPlaying]);

    return (
        <Root onClick={handlePlay}>
            <Video
                ref={ref}
                autoPlay={autoPlay}
                preload="auto"
                muted={muted}
                loop={loop}
                src={source}
                controls={false}
                playsInline={true}
                poster={poster}
                width={width}
                height={height}
                borderRadius={borderRadius}
                aspectRadio={aspectRadio}
            />
            <Play id="product-video--button--play" onClick={handlePlay}>
                {!playing && <Icon style={{ fontSize: 56 }}>play_circle</Icon>}
            </Play>
        </Root>
    );
};
